import { useEffect, useRef, useState } from "react";

/**
 * useDebounce — returns `value` delayed by `delay` ms (default 300).
 *
 * Used by the AllUsers search filters: previously every keystroke fired a
 * GraphQL query (typing a 10-char name = 10 backend searches). With this,
 * the query fires only after the user stops typing.
 *
 * Empty-value fast path: clearing a search flushes immediately.
 */
export default function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (value === "") {
      setDebounced(value);
      return undefined;
    }
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * useDebouncedCallback — stable debounced wrapper for event handlers.
 */
export function useDebouncedCallback(callback, delay = 300) {
  const timeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  return (...args) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => callback(...args), delay);
  };
}
